import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'common/styledComponents/typography';
import { PageButton, PageGroup, PaginationBar } from 'modules/settings/components/DataExport/styled';

interface Props {
  /** Zero-based. */
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}

const SIBLINGS = 1;

/** The page numbers to show: always the first and last, the current one with its neighbours, `null` for a gap. */
export const pageItems = (current: number, count: number): (number | null)[] => {
  const wanted = new Set([0, count - 1, current, ...Array.from({ length: SIBLINGS * 2 + 1 }, (_, i) => current - SIBLINGS + i)]);
  const pages = [...wanted].filter(p => p >= 0 && p < count).sort((a, b) => a - b);

  return pages.flatMap((p, i) => (i > 0 && p - pages[i - 1] > 1 ? [null, p] : [p]));
};

/** A joined "Previous 1 2 3 Next" group on the left and the range on the right. Renders nothing when one page is enough. */
const Pagination: React.FC<Props> = ({ page, pageSize, total, onChange }) => {
  const { t } = useTranslation();
  if (total <= pageSize) return null;

  const count = Math.ceil(total / pageSize);
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);

  return (
    <PaginationBar data-testid='data-export-pagination'>
      <PageGroup aria-label={t('dataExport.pagination.label')}>
        <PageButton type='button' disabled={page === 0} onClick={() => onChange(page - 1)}>
          {t('dataExport.pagination.previous')}
        </PageButton>
        {pageItems(page, count).map((item, index) =>
          item === null ? (
            <PageButton key={`gap-${index}`} type='button' disabled aria-hidden='true'>
              …
            </PageButton>
          ) : (
            <PageButton
              key={item}
              type='button'
              aria-current={item === page ? 'page' : undefined}
              aria-label={t('dataExport.pagination.page', { page: item + 1 })}
              onClick={() => onChange(item)}
            >
              {item + 1}
            </PageButton>
          ),
        )}
        <PageButton type='button' disabled={page === count - 1} onClick={() => onChange(page + 1)}>
          {t('dataExport.pagination.next')}
        </PageButton>
      </PageGroup>
      <Text variant='body2' color='black50'>
        {t('dataExport.pagination.range', { from, to, total })}
      </Text>
    </PaginationBar>
  );
};

export default React.memo(Pagination);
